import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgxMaskDirective } from 'ngx-mask';
import { RecadastramentoApiImpl } from './data-access/api/recadastramento-api-impl';
import { Router } from '@angular/router';
import { UserType } from '../../data-access/models/user.model';
import { ResendOtpApiImpl } from '../../data-access/api/resend-otp-impl';
import { CountrySelectComponent } from './ui/country-select/country-select';
import { DEFAULT_LANG, UNKNOWN_COUNTRY } from '../../services/language';
import { PhoneMaskStrategy } from '../../core/masks/phone-mask';
import { DocumentMaskStrategy } from '../../core/masks/document-mask';
import { DocumentValidatorDirective } from '../../core/validators/document-validator';
import { CountryConfigurationService } from '../../core/country/country-configuration';
import { TokenClaimsService } from '../../services/token-claims';
import { RedirectUriService } from '../../auth/redirect-uri';
import { LanguageReadyService } from '../../services/language-ready';
import { LangGateComponent } from '../../shared/ui/lang-gate/lang-gate';
import { TextSkeletonComponent } from '../../shared/ui/text-skeleton/text-skeleton';

@Component({
  selector: 'app-recadastramento',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    NgxMaskDirective,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatButtonModule,
    MatCardModule,
    MatSelectModule,
    TranslatePipe,
    MatIconModule,
    CountrySelectComponent,
    DocumentValidatorDirective,
    TextSkeletonComponent,
    LangGateComponent,
  ],
  templateUrl: './recadastramento.html',
  styleUrl: './recadastramento.scss',
})
export class Recadastramento implements OnInit {
  constructor(
    private readonly api: ResendOtpApiImpl,
    private readonly recadastramentoApi: RecadastramentoApiImpl,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly phoneMaskStrategy: PhoneMaskStrategy,
    private readonly documentMaskStrategy: DocumentMaskStrategy,
    private readonly countryConfig: CountryConfigurationService,
    private readonly tokenClaims: TokenClaimsService,
    private readonly redirectUriService: RedirectUriService,
    protected readonly langReady: LanguageReadyService,
  ) {
    this.restoreRegistrationState();
  }

  ngOnInit(): void {
    void this.loadInitialData();
  }

  private async loadInitialData(): Promise<void> {
    const initialName = await this.tokenClaims.getFieldValueFromKey('name');
    const initialEmail = await this.tokenClaims.getFieldValueFromKey('email');

    this.model.update(currentState => ({
      ...currentState,
      name: String(initialName),
      email: String(initialEmail)
    }));

    void this.initLanguage();
  }

  private async initLanguage(): Promise<void> {
    const detected = await this.langReady.ensure('common');
    if (detected) {
      this.detectedLocale.set(detected.lang);
      if (detected.country && !this.model().country) this.onCountryChange(detected.country);
    }
    this.phoneFallbackPlaceholder.set(
      this.translate.instant('FORM_RE_REGISTRATION.FIELDS.CELLPHONE.PLACEHOLDER'),
    );
    this.documentFallbackPlaceholder.set(
      this.translate.instant('FORM_RE_REGISTRATION.FIELDS.DOCUMENT.PLACEHOLDER'),
    );
    this.namePlaceholder.set(this.translate.instant('FORM_RE_REGISTRATION.FIELDS.NAME.PLACEHOLDER'));
    this.emailPlaceholder.set(
      this.translate.instant('FORM_RE_REGISTRATION.FIELDS.EMAIL.PLACEHOLDER'),
    );
  }

  model = signal<UserType>({
    name: '',
    phone: '',
    document: '',
    email: '',
    country: null,
    language: DEFAULT_LANG,
    termsAccepted: false,
  });

  detectedLocale = signal<string>('');
  isEmailInUse = signal<boolean>(false);
  isSendingOtp = signal<boolean>(false);
  requestError = signal<boolean>(false);

  names = signal<string[]>([]);
  firstName = signal<string>('');
  lastName = signal<string>('');
  username = signal<string>('');
  document = signal<string>('');

  clioid = signal<number>(0);
  usuoid = signal<number>(0);
  usufilho = signal<number>(0);
  phone = signal<number>(0);

  readonly phoneFallbackPlaceholder = signal<string>('');
  readonly documentFallbackPlaceholder = signal<string>('');
  readonly namePlaceholder = signal<string>('');
  readonly emailPlaceholder = signal<string>('');

  readonly phoneMask = computed(() => this.phoneMaskStrategy.getMask(this.model().country));
  readonly phonePrefix = computed(() => this.phoneMaskStrategy.getPrefix(this.model().country));
  readonly phonePlaceholder = computed(() => {
    const country = this.model().country;
    if (!country || country === UNKNOWN_COUNTRY) return this.phoneFallbackPlaceholder();
    return this.phoneMaskStrategy.getPlaceholder(country);
  });

  readonly documentMask = computed(() => this.documentMaskStrategy.getMask(this.model().country));
  readonly documentType = computed(() => this.documentMaskStrategy.getType(this.model().country));
  readonly documentPlaceholder = computed(() => {
    const country = this.model().country;
    if (!country || country === UNKNOWN_COUNTRY) return this.documentFallbackPlaceholder();
    return this.documentMaskStrategy.getPlaceholder(country);
  });

  updateField<K extends keyof UserType>(key: K, value: UserType[K]): void {
    this.model.update((m) => ({ ...m, [key]: value }));
  }

  async verifyEmail(email: string) {
    let username = await this.tokenClaims.getFieldValueFromKey('preferred_username');

    if (email.trim() && username.length > 0) {
      this.requestError.set(false);
      this.recadastramentoApi.checkingIfEmailIsInUse({ email, username }).subscribe({
        next: (res) => {
          this.isEmailInUse.set(res.isEmailAlreadyInUse);
        },
        error: () => {
          this.requestError.set(true);
        },
      });
    }
  }

  goToGestaoFrotas() {
    const flutterToken = sessionStorage.getItem('flutter_token') ||
      sessionStorage.getItem('auth_token') ||
      sessionStorage.getItem('kc_token');

    const isMobile = !!flutterToken;

    if (isMobile) {
      (globalThis.window as any).Flutter?.postMessage(JSON.stringify({
        action: 'close'
      }));
      return;
    }
    globalThis.location.href = this.redirectUriService.getRedirectUri();
  }

  async onNext(): Promise<void> {
    if (this.isSendingOtp()) return;

    await this.setFullName(this.model().name);
    this.username.set(await this.tokenClaims.getFieldValueFromKey('preferred_username'));
    this.clioid.set(await this.tokenClaims.getFieldValueFromKey('clioid'));
    this.usuoid.set(await this.tokenClaims.getFieldValueFromKey('usuoid'));
    this.usufilho.set(await this.tokenClaims.getFieldValueFromKey('usufilho'));

    this.isSendingOtp.set(true);
    this.requestError.set(false);
    const locale = this.detectedLocale() || undefined;
    this.api.resendOtp({ email: this.model().email, locale }).subscribe({
      next: () => {
        this.isSendingOtp.set(false);
        this.router.navigate(['/otp'], {
          state: {
            email: this.model().email,
            fromRecadastramento: true,
            registrationState: {
              model: { ...this.model() },
              isEmailInUse: this.isEmailInUse(),
              clioid: this.clioid(),
              usuoid: this.usuoid(),
              usufilho: this.usufilho(),
              username: this.username(),
              firstName: this.firstName(),
              lastName: this.lastName(),
              detectedLocale: locale || undefined,
            },
          },
        });
      },
      error: () => {
        this.isSendingOtp.set(false);
        this.requestError.set(true);
      },
    });
  }

  async setFullName(name: string): Promise<void> {
    this.names.set(name.split(' '));

    if (this.names().length > 1) {
      this.firstName.set(this.names()[0]);
      this.names.update((names) => names.slice(1));

      for (let i = 0; i < this.names().length; i++) {
        this.lastName.update((lastName) => lastName + this.names()[i] + ' ');
      }
      this.lastName.set(this.lastName().trim());
    } else {
      this.firstName.set(name);
    }
  }

  private restoreRegistrationState(): void {
    const state = this.getRouteState();
    const registrationState = state?.['registrationState'];
    if (!registrationState || typeof registrationState !== 'object') return;

    const model = (registrationState as { model?: UserType }).model;
    if (model) {
      this.model.update((current) => ({ ...current, ...model }));
    }

    const isEmailInUse = (registrationState as { isEmailInUse?: unknown }).isEmailInUse;
    if (typeof isEmailInUse === 'boolean') {
      this.isEmailInUse.set(isEmailInUse);
    }
  }

  private getRouteState(): Record<string, unknown> | undefined {
    return (
      this.router.getCurrentNavigation?.()?.extras.state ??
      (typeof globalThis.window !== 'undefined' ? globalThis.history.state : undefined)
    );
  }

  onCountryChange(countryCode: string | null): void {
    const language = this.countryConfig.getConfig(countryCode)?.locale ?? DEFAULT_LANG;
    this.model.update((m) => ({ ...m, country: countryCode, language, phone: '', document: '' }));
  }
}
